import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { InputTextChipInterface } from '../../../types/interfaces/input.interface';
import { Skeleton } from 'primeng/skeleton';
import { TranslateSafePipe } from '../../pipes/translation.pipe';

@Component({
  selector: 'app-input-text-chip',
  imports: [
    ReactiveFormsModule,
    Skeleton,
    TranslateSafePipe,
  ],
  templateUrl: './input-text-chip.component.html',
  styleUrls: [
    '../../styles/input-base.css',
    './input-text-chip.component.css',
  ],
})
export class InputTextChipComponent {
  @Input()
  public formData!: InputTextChipInterface;

  @Input()
  public isLoading = false;

  @ViewChild('tagInput')
  public tagInput?: ElementRef<HTMLInputElement>;

  public inputValue = '';

  private editIndex: number | null = null;

  public get control(): FormControl<string[]> {
    return this.formData.formGroup?.get(this.formData.id) as FormControl<string[]>;
  }

  public get emailList(): string[] {
    let emails: string[] = this.formData.predefinedValues ?? [];

    return emails.filter((email) => !this.tags.includes(email));
  }

  public get tags(): string[] {
    return this.control?.value ?? [];
  }

  public focusInput(): void {
    this.tagInput?.nativeElement.focus();
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.formData.readonly) {
      return;
    }

    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.saveTag();
      return;
    }

    if (
      event.key === 'Backspace' &&
      !this.inputValue.trim() &&
      this.tags.length > 0
    ) {
      event.preventDefault();

      const index = this.tags.length - 1;
      const value = this.tags[index];

      this.removeTag(index);

      this.inputValue = value;

      this.editIndex = null;

      setTimeout(() => this.focusInput());
    }

    if (event.key === 'Escape' && this.editIndex !== null) {
      this.cancelEdit();
    }
  }

  public saveTag(): void {
    const value = this.inputValue.trim();

    if (!value) {
      return;
    }

    if (this.formData.isValid && !this.formData.isValid(value)) {
      return;
    }

    const updatedTags = [...this.tags];

    if (this.editIndex !== null) {
      updatedTags[this.editIndex] = value;
    } else {
      if (updatedTags.includes(value)) {
        this.inputValue = '';
        return;
      }

      updatedTags.push(value);
    }

    this.control.setValue(updatedTags);
    this.control.markAsDirty();
    this.control.markAsTouched();

    this.inputValue = '';
    this.editIndex = null;
  }

  public editTag(index: number): void {
    if (this.formData.readonly) {
      return;
    }

    const value = this.tags[index];

    if (!value) {
      return;
    }

    this.editIndex = index;

    this.inputValue = value;

    setTimeout(() => {
      this.focusInput();
      this.tagInput?.nativeElement.select();
    });
  }

  public cancelEdit(): void {
    this.inputValue = '';
    this.editIndex = null;
  }

  public removeTag(index: number): void {
    const updatedTags = this.tags.filter((_, i) => i !== index);

    this.control.setValue(updatedTags);
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  public onBlur(): void {
    if (this.inputValue.trim()) {
      this.saveTag();
    }

    this.control.markAsTouched();
  }

  public onInput(event?: Event): void {
    this.formData.onInput?.(event);
  }
}